import * as React from 'react';
import { View } from 'react-native';

import type * as api from '@gitbook/api-client';
import { flex, spacing } from '@gitbook/react-uikit';

import type { ContentKitElementProps } from './types';

export const ContentKitStackDirectionContent = React.createContext<'hstack' | 'vstack'>('vstack');

/**
 * Component to render a vstack or hstack element.
 */
export function ContentKitStack(
    props: ContentKitElementProps<api.ContentKitHStack | api.ContentKitVStack>
): React.ReactElement {
    const { element, renderElement } = props;

    return (
        <ContentKitStackDirectionContent.Provider value={element.type}>
            <View
                style={[
                    element.type === 'hstack' ? flex.row : flex.column,
                    element.type === 'hstack' ? flex.alignItemsCenter : null,
                    element.type === 'vstack' && element.align === 'end' && flex.alignItemsEnd,
                    element.type === 'vstack' &&
                        element.align === 'center' &&
                        flex.alignItemsCenter,
                    element.type === 'hstack' && element.align === 'end' && flex.justifyEnd,
                    element.type === 'hstack' && element.align === 'center' && flex.justifyCenter,
                ]}
            >
                {element.children.map((child, index) => {
                    const next = element.children[index + 1];
                    const spacingKey = next ? getStackElementsSpacing(child, next) : null;

                    return (
                        <View
                            key={index}
                            style={[
                                child.type === 'box' ? { flexGrow: child.grow || 0 } : null,
                                spacingKey
                                    ? element.type === 'hstack'
                                        ? spacing.Padding[`right${spacingKey}`]
                                        : spacing.Padding[`bottom${spacingKey}`]
                                    : null,
                            ]}
                        >
                            {renderElement(child)}
                        </View>
                    );
                })}
            </View>
        </ContentKitStackDirectionContent.Provider>
    );
}

/**
 * Ensure a set of elements are going to be rendered as a stack (either hstack or vstack).
 * If the elements are already a stack, return them as is.
 */
export function ensureStackElements(
    type: 'hstack' | 'vstack',
    elements: api.ContentKitDescendantElement[],
    align?: 'start' | 'center' | 'end'
): api.ContentKitDescendantElement[] {
    if (elements.length === 1) {
        return elements;
    }

    return [{ type, align, children: elements }];
}

/**
 * Get the spacing between two elements in a stack.
 */
function getStackElementsSpacing(
    a: api.ContentKitDescendantElement,
    b: api.ContentKitDescendantElement
): '200' | '400' | '600' | null {
    if (a.type === 'divider' || b.type === 'divider') {
        return null;
    }

    if (a.type === 'input' || b.type === 'input' || a.type === 'box' || b.type === 'box') {
        return '400';
    }

    return '200';
}
